import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';

import { expVal } from '@confluence/feature-experiments';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';

const NO_HIDDEN_OPTIONS: readonly RemixHiddenOptionKind[] = [];

/**
 * Diagrams finalize as whiteboards, so hide them from every Remix picker
 * (editor, object sidebar, and renderer) when Whiteboards are unavailable.
 * The object-sidebar picker also hides Whiteboard, Database, and Slides when
 * their corresponding space settings have not confirmed they are enabled.
 */
export const useShouldHideDiagramRemixOption = (): readonly RemixHiddenOptionKind[] => {
	const [spaceKey] = usePageSpaceKey();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: true,
	});

	const isWhiteboardsEnabled =
		spaceDetail?.typeSettings?.enabledContentTypes?.isWhiteboardsEnabled ?? false;
	const enabledContentTypes = spaceDetail?.typeSettings?.enabledContentTypes;
	const hiddenOptions: RemixHiddenOptionKind[] = [];

	// Only hide the diagram option when whiteboards are unavailable for the space
	if (!isWhiteboardsEnabled && expVal('cc_maui_polish_changes_batch_2', 'isEnabled', false)) {
		hiddenOptions.push('diagram');
	}

	if (!enabledContentTypes?.isWhiteboardsEnabled) {
		hiddenOptions.push('whiteboard');
	}
	if (!enabledContentTypes?.isDatabasesEnabled) {
		hiddenOptions.push('database');
	}
	if (!enabledContentTypes?.isSlidesEnabled) {
		hiddenOptions.push('slides');
	}

	return hiddenOptions.length > 0 ? hiddenOptions : NO_HIDDEN_OPTIONS;
};
